/**
 * Supported source/target languages for decks and cards.
 *
 * IDs are ISO 639-1 lowercase. They become foreign keys for Card.lang fields
 * in the future concept_id-based store (see docs/ARCHITECTURE.md).
 *
 * The `name` field is localized — the language display name in each of the
 * UI locales we ship. `native` stays a single string (the endonym is fixed).
 */

import type { Localized } from "./locale";

export type LanguageId =
  | "ru"
  | "en"
  | "zh"
  | "ja"
  | "es"
  | "fr"
  | "de"
  | "ar"
  | "hi"
  | "ur"
  | "id"
  | "pt"
  | "it"
  | "ko"
  | "tr";

export type ScriptDirection = "ltr" | "rtl";

export type LanguageRef = {
  id: LanguageId;
  /** Localized display name. */
  name: Localized;
  /** Native endonym — shown alongside the localized name for clarity. */
  native: string;
  /** Flag emoji as a quick visual marker. */
  flag: string;
  direction: ScriptDirection;
  /** ISO 639-2/3 code where it differs from id — handy for future TTS / NLP. */
  iso3?: string;
};

export const LANGUAGES: LanguageRef[] = [
  {
    id: "ru",
    name: { ru: "Русский", en: "Russian", es: "Ruso", fr: "Russe", de: "Russisch", it: "Russo", zh: "俄语", ja: "ロシア語" },
    native: "Русский", flag: "🇷🇺", direction: "ltr", iso3: "rus",
  },
  {
    id: "en",
    name: { ru: "Английский", en: "English", es: "Inglés", fr: "Anglais", de: "Englisch", it: "Inglese", zh: "英语", ja: "英語" },
    native: "English", flag: "🇬🇧", direction: "ltr", iso3: "eng",
  },
  {
    id: "es",
    name: { ru: "Испанский", en: "Spanish", es: "Español", fr: "Espagnol", de: "Spanisch", it: "Spagnolo", zh: "西班牙语", ja: "スペイン語" },
    native: "Español", flag: "🇪🇸", direction: "ltr", iso3: "spa",
  },
  {
    id: "fr",
    name: { ru: "Французский", en: "French", es: "Francés", fr: "Français", de: "Französisch", it: "Francese", zh: "法语", ja: "フランス語" },
    native: "Français", flag: "🇫🇷", direction: "ltr", iso3: "fra",
  },
  {
    id: "de",
    name: { ru: "Немецкий", en: "German", es: "Alemán", fr: "Allemand", de: "Deutsch", it: "Tedesco", zh: "德语", ja: "ドイツ語" },
    native: "Deutsch", flag: "🇩🇪", direction: "ltr", iso3: "deu",
  },
  {
    id: "it",
    name: { ru: "Итальянский", en: "Italian", es: "Italiano", fr: "Italien", de: "Italienisch", it: "Italiano", zh: "意大利语", ja: "イタリア語" },
    native: "Italiano", flag: "🇮🇹", direction: "ltr", iso3: "ita",
  },
  {
    id: "pt",
    name: { ru: "Португальский", en: "Portuguese", es: "Portugués", fr: "Portugais", de: "Portugiesisch", it: "Portoghese", zh: "葡萄牙语", ja: "ポルトガル語" },
    native: "Português", flag: "🇵🇹", direction: "ltr", iso3: "por",
  },
  {
    id: "tr",
    name: { ru: "Турецкий", en: "Turkish", es: "Turco", fr: "Turc", de: "Türkisch", it: "Turco", zh: "土耳其语", ja: "トルコ語" },
    native: "Türkçe", flag: "🇹🇷", direction: "ltr", iso3: "tur",
  },
  {
    id: "zh",
    name: { ru: "Китайский", en: "Chinese", es: "Chino", fr: "Chinois", de: "Chinesisch", it: "Cinese", zh: "中文", ja: "中国語" },
    native: "中文", flag: "🇨🇳", direction: "ltr", iso3: "zho",
  },
  {
    id: "ja",
    name: { ru: "Японский", en: "Japanese", es: "Japonés", fr: "Japonais", de: "Japanisch", it: "Giapponese", zh: "日语", ja: "日本語" },
    native: "日本語", flag: "🇯🇵", direction: "ltr", iso3: "jpn",
  },
  {
    id: "ko",
    name: { ru: "Корейский", en: "Korean", es: "Coreano", fr: "Coréen", de: "Koreanisch", it: "Coreano", zh: "韩语", ja: "韓国語" },
    native: "한국어", flag: "🇰🇷", direction: "ltr", iso3: "kor",
  },
  {
    id: "ar",
    name: { ru: "Арабский", en: "Arabic", es: "Árabe", fr: "Arabe", de: "Arabisch", it: "Arabo", zh: "阿拉伯语", ja: "アラビア語" },
    native: "العربية", flag: "🇸🇦", direction: "rtl", iso3: "ara",
  },
  {
    id: "hi",
    name: { ru: "Хинди", en: "Hindi", es: "Hindi", fr: "Hindi", de: "Hindi", it: "Hindi", zh: "印地语", ja: "ヒンディー語" },
    native: "हिन्दी", flag: "🇮🇳", direction: "ltr", iso3: "hin",
  },
  {
    id: "ur",
    name: { ru: "Урду", en: "Urdu", es: "Urdu", fr: "Ourdou", de: "Urdu", it: "Urdu", zh: "乌尔都语", ja: "ウルドゥー語" },
    native: "اردو", flag: "🇵🇰", direction: "rtl", iso3: "urd",
  },
  {
    id: "id",
    name: { ru: "Индонезийский", en: "Indonesian", es: "Indonesio", fr: "Indonésien", de: "Indonesisch", it: "Indonesiano", zh: "印尼语", ja: "インドネシア語" },
    native: "Bahasa Indonesia", flag: "🇮🇩", direction: "ltr", iso3: "ind",
  },
];

export const LANGUAGE_BY_ID: Record<LanguageId, LanguageRef> = Object.fromEntries(
  LANGUAGES.map((l) => [l.id, l]),
) as Record<LanguageId, LanguageRef>;

export function isLanguageId(value: unknown): value is LanguageId {
  return typeof value === "string" && value in LANGUAGE_BY_ID;
}

/**
 * Map a BCP-47-ish language tag (e.g. Telegram's `language_code` or
 * `navigator.language`, like "en-US" or "pt") onto a supported
 * {@link LanguageId}. Returns null when the base subtag isn't one we ship.
 */
export function mapCodeToLanguageId(code: string | null | undefined): LanguageId | null {
  if (!code) return null;
  const base = code.toLowerCase().split(/[-_]/)[0];
  return isLanguageId(base) ? base : null;
}
